<template lang="pug">
  aiq-input(v-model="formattedInputValue"
            :disabled="disabled"
            :type="type"
            :name="name"
            :size="size"
            :placeholder="placeholder"
            @focus="$emit('focus')"
            @blur="$emit('blur')"
            @keyup.enter="handleEnter")
    template(v-if="$slots.prepend" v-slot:prepend)
      slot(name="prepend")
</template>

<script>
import debounce from 'lodash/debounce';

const INPUT_MAX_LENGTH = 256;
const DEBOUNCE_TIMEOUT = 700;

export default {
  name: 'FilteredInput',
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'text',
    },
    name: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'small',
    },
    maxLength: {
      type: Number,
      default: INPUT_MAX_LENGTH,
    },
    placeholder: {
      type: String,
      default: '',
    },
    modelValue: {
      type: String,
      default: '',
    },
    regex: {
      type: String,
      default: '',
    },
    shouldFilterSpaces: {
      type: Boolean,
      default: false,
    },
    debounceTimeout: {
      type: Number,
      default: DEBOUNCE_TIMEOUT,
    },
    emitOnInput: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      inputValue: this.modelValue,
    };
  },
  emits: ['update:modelValue', 'blur', 'focus'],
  computed: {
    formattedInputValue: {
      get() {
        return this.inputValue;
      },
      set(value) {
        this.inputValue = value;
        if (this.emitOnInput) {
          this.update();
        }
      },
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        const next = newVal ?? '';
        if (next !== this.inputValue) {
          this.inputValue = next;
        }
      },
    },
  },
  created() {
    this.update = debounce(this._update, this.debounceTimeout);
  },
  methods: {
    commitValue() {
      if (this.update?.cancel) {
        this.update.cancel();
      }
      this._update();
    },
    handleEnter() {
      if (!this.emitOnInput) {
        this.commitValue();
      }
    },
    _update() {
      const inputValue = this.formatter(this.inputValue);
      if (inputValue !== this.inputValue) {
        this.inputValue = inputValue;
        if (inputValue.length >= this.maxLength) {
          this.$aiq.notify.error(this.$t('metrics_tab.search.notify_errors.max_input_exceeded'));
        } else {
          const errorKey = this.shouldFilterSpaces
            ? 'metrics_tab.search.notify_errors.no_special_characters_or_spaces'
            : 'metrics_tab.search.notify_errors.no_special_characters';
          this.$aiq.notify.error(this.$t(errorKey));
        }
      }
      this.$emit('update:modelValue', this.inputValue);
    },
    formatter(value) {
      // eslint-disable-next-line no-useless-escape
      const regex = this.shouldFilterSpaces ? /[\s\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@#$%&\\\=\(\'\"]/gi : /[\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@#$%&\\\=\(\'\"]/gi;
      const result = value.replace(this.regex || regex, '');
      return result.substring(0, this.maxLength);
    },
  },
};
</script>
